import { html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/select/wow-select";
import "ui-components/form-elements/switch/switch";
import "ui-components/collapsible-section/collapsible-section";
import "ui-components/button/wow-button";

import { styles } from "./live-event-optional-toggles.css";
import { SignalElement, inject } from "ui-common/di";
import type {
  ILiveEventFormState,
  ILiveEventFormService,
  UIToggleState,
} from "ui-common/services/live-event-form/types";
import { handleFormInput } from "ui-common/form/form-utils";
import { t } from "../../../translations/i18n";

@customElement("live-event-optional-toggles")
export class LiveEventOptionalToggles extends SignalElement {
  static override styles = [styles];

  @inject<ILiveEventFormState>("liveEventFormState")
  private state!: ILiveEventFormState;

  @inject<ILiveEventFormService>("liveEventFormService")
  private service!: ILiveEventFormService;

  @state()
  private extraSelectCount = 0;

  private handleToggle(toggle: keyof UIToggleState, event: CustomEvent): void {
    const checked = event.detail.checked;
    this.service.updateUIToggle(toggle, checked);
  }

  private handleLinkSelect(index: number, event: CustomEvent): void {
    const newLinkId = event.detail.value as string;
    if (!newLinkId) return;

    const linkedLinkIds = this.state.linkedLinkIds.get();
    const currentLinkId = linkedLinkIds[index];

    // Remove old selection at this index if exists
    if (currentLinkId) {
      this.service.toggleLinkedLink(currentLinkId, false);
    }

    // Add new selection
    this.service.toggleLinkedLink(newLinkId, true);
  }

  private handleRemoveLink(index: number): void {
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const linkId = linkedLinkIds[index];
    if (linkId) {
      this.service.toggleLinkedLink(linkId, false);
    }
    if (this.extraSelectCount > 0) {
      this.extraSelectCount--;
    }
  }

  private handleAddExtra(): void {
    this.extraSelectCount++;
  }

  private getAvailableOptionsForIndex(index: number) {
    const availableLinks = this.state.availableLinks.get();
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const currentValue = linkedLinkIds[index];

    const linkOptions = availableLinks
      .filter((link) => !linkedLinkIds.includes(link.id) || link.id === currentValue)
      .map((link) => ({
        value: link.id,
        label: link.title,
      }));

    return [{ value: "", label: "" }, ...linkOptions];
  }

  private renderLinkedLinks() {
    const availableLinks = this.state.availableLinks.get();
    const linkedLinkIds = this.state.linkedLinkIds.get();
    const isLoadingLinks = this.state.isLoadingLinks.get();

    if (isLoadingLinks) {
      return html`<div class="loading-links">${t("createLiveEventPage.loadingLinks")}</div>`;
    }

    if (availableLinks.length === 0) {
      return html`<div class="no-links">${t("createLiveEventPage.noLinksAvailable")}</div>`;
    }

    const selectCount = Math.max(1, linkedLinkIds.length) + this.extraSelectCount;
    const hasMoreLinksAvailable = linkedLinkIds.length < availableLinks.length;

    return html`
      <div class="link-selects">
        ${Array.from({ length: selectCount }, (_, i) => {
          const options = this.getAvailableOptionsForIndex(i);
          const currentValue = linkedLinkIds[i] || "";
          const showRemove = i > 0 || linkedLinkIds.length > 1;

          return html`
            <div class="link-select-row">
              <wow-select
                label=${t("createLiveEventPage.selectLink")}
                .options=${options}
                .value=${currentValue}
                @change=${(e: CustomEvent) => this.handleLinkSelect(i, e)}
              ></wow-select>
              ${showRemove
                ? html`
                    <wow-button variant="subtle" size="small" square @click=${() => this.handleRemoveLink(i)}>
                      &times;
                    </wow-button>
                  `
                : nothing}
            </div>
          `;
        })}
        ${hasMoreLinksAvailable
          ? html`
              <button class="add-extra-button" @click=${this.handleAddExtra}>
                ${t("createLiveEventPage.addExtra")}
              </button>
            `
          : nothing}
      </div>
    `;
  }

  override render() {
    const formData = this.state.formData.get();
    const uiToggles = this.state.uiToggles.get();

    return html`
      <div class="optional-toggles">
        <hr class="section-divider" />
        <wow-collapsible-section
          collapsible
          header=${t("createLiveEventPage.linkDiscounts")}
          subtitle=${t("createLiveEventPage.linkDiscountsSubtitle")}
        >
          ${this.renderLinkedLinks()}
        </wow-collapsible-section>

        <!-- WhatsApp Community Toggle -->
        <hr class="section-divider" />
        <div class="toggle-field">
          <wow-switch
            label=${t("createLiveEventPage.whatsappCommunity")}
            ?checked=${uiToggles.showWhatsApp}
            @change=${(e: CustomEvent) => this.handleToggle("showWhatsApp", e)}
          ></wow-switch>
          ${uiToggles.showWhatsApp
            ? html`
                <wow-input
                  type="url"
                  placeholder=${t("createLiveEventPage.whatsappGroupLink")}
                  .value=${formData.whatsappUrl}
                  @input=${(e: Event) => handleFormInput(this.service, "whatsappUrl", e)}
                ></wow-input>
              `
            : nothing}
        </div>

        <!-- CTA Text Toggle -->
        <hr class="section-divider" />
        <div class="toggle-field">
          <wow-switch
            label=${t("createLiveEventPage.addCtaText")}
            ?checked=${uiToggles.showCtaText}
            @change=${(e: CustomEvent) => this.handleToggle("showCtaText", e)}
          ></wow-switch>
          ${uiToggles.showCtaText
            ? html`
                <wow-input
                  placeholder=${t("createLiveEventPage.ctaPlaceholder")}
                  maxlength="255"
                  .value=${formData.ctaText}
                  @input=${(e: Event) => handleFormInput(this.service, "ctaText", e)}
                ></wow-input>
              `
            : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-event-optional-toggles": LiveEventOptionalToggles;
  }
}
